import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UtensilsCrossed, ShoppingBag, Plus, Minus, Tag } from 'lucide-react';
import { Button } from './ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ProductDetailModal = ({ item, isOpen, onClose, onAddToCart, language = 'tr' }) => {
  const [qty, setQty] = React.useState(1);

  React.useEffect(() => { if (isOpen) setQty(1); }, [isOpen]);

  if (!item) return null;

  const texts = {
    tr: { add: 'Sepete Ekle', category: 'Kategori', price: 'Fiyat', outOfStock: 'Tükendi', stock: 'Stok', description: 'Ürün Detayı' },
    en: { add: 'Add to Cart', category: 'Category', price: 'Price', outOfStock: 'Out of Stock', stock: 'Stock', description: 'Product Detail' },
    me: { add: 'Dodaj u Korpu', category: 'Kategorija', price: 'Cijena', outOfStock: 'Nema', stock: 'Zaliha', description: 'Detalji Proizvoda' }
  };
  const txt = texts[language] || texts.tr;

  const name = item._displayName || item.name;
  const desc = item._displayDesc || item.description || '';
  const imageUrl = item.image_url
    ? (item.image_url.startsWith('/api') ? `${API_BASE}${item.image_url}` : item.image_url)
    : null;
  const isAvailable = item.is_available !== false && (item.stock === undefined || item.stock > 0);
  const category = item._displayCategory || item.category || '';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-charcoal/70 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-white rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="product-detail-modal"
          >
            {/* Image */}
            <div className="relative aspect-[16/10] bg-stone-100 overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : null}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <UtensilsCrossed className="w-16 h-16 text-stone-200" />
              </div>
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-md"
                data-testid="close-product-modal"
              >
                <X className="w-5 h-5 text-charcoal" />
              </button>
              {!isAvailable && (
                <div className="absolute inset-0 bg-charcoal/50 flex items-center justify-center">
                  <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold">{txt.outOfStock}</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              {category && (
                <div className="flex items-center gap-1.5 mb-2">
                  <Tag className="w-3.5 h-3.5 text-basil" />
                  <span className="text-xs font-medium text-basil uppercase tracking-wide">{category}</span>
                </div>
              )}

              <h2 className="font-playfair text-2xl font-bold text-charcoal mb-1" data-testid="product-detail-name">
                {name}
              </h2>

              <p className="text-terracotta font-bold text-2xl mb-4" data-testid="product-detail-price">
                €{(item.price || 0).toFixed(2)}
              </p>

              {desc && (
                <div className="mb-5">
                  <p className="text-charcoal/70 leading-relaxed" data-testid="product-detail-description">
                    {desc}
                  </p>
                </div>
              )}

              {item.stock !== undefined && item.stock > 0 && (
                <p className="text-sm text-charcoal/50 mb-4">{txt.stock}: {item.stock}</p>
              )}

              {/* Add to Cart */}
              {isAvailable && onAddToCart && (
                <div className="flex items-center gap-3 pt-2 border-t border-stone-100">
                  <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
                    <button
                      onClick={() => setQty(Math.max(1, qty - 1))}
                      className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 shadow-sm"
                      data-testid="modal-qty-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-bold">{qty}</span>
                    <button
                      onClick={() => setQty(qty + 1)}
                      className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 shadow-sm"
                      data-testid="modal-qty-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <Button
                    onClick={() => { onAddToCart(item, qty); onClose(); }}
                    className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-5 text-base font-semibold"
                    data-testid="modal-add-to-cart"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    {txt.add} · €{(item.price * qty).toFixed(2)}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductDetailModal;
